import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Sheet,
  Stack,
  Textarea,
  Typography,
  Select,
  Option,
  FormControl,
  FormLabel,
} from '@mui/joy'
import DateRangePicker from '../components/DateRangePicker'
import {
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Send as SendIcon,
  CheckCircle as ResolvedIcon,
} from '@mui/icons-material'
import api from '../services/api'

interface WebChatWidget {
  id: number
  name: string
}

interface WebChatConversation {
  id: number
  uuid: string
  widgetId: number
  sessionId: string
  visitorName: string
  status: 'open' | 'resolved' | 'closed'
  unreadMessages: number
  lastMessage?: string
  lastMessageAt?: string
  createdAt: string
  updatedAt: string
  widget?: WebChatWidget
  metadata?: Record<string, any>
}

interface WebChatMessage {
  id: number
  conversationId: number
  direction: 'inbound' | 'outbound'
  body: string
  senderId?: number
  sender?: { id: number; name: string }
  createdAt: string
}

export default function WebChatChats() {
  const [conversations, setConversations] = useState<WebChatConversation[]>([])
  const [selected, setSelected] = useState<WebChatConversation | null>(null)
  const [messages, setMessages] = useState<WebChatMessage[]>([])
  const [message, setMessage] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  // Filtro por rango de fechas (default: últimos 30 días), igual que en Tickets.
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().slice(0, 10)
  })
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const selectedId = selected?.id

  useEffect(() => {
    void fetchConversations()
  }, [status, startDate, endDate])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!selectedId) return

    const timer = window.setInterval(() => {
      void fetchMessages(selectedId, false)
      void fetchConversations(false)
    }, 5000)

    return () => window.clearInterval(timer)
  }, [selectedId, status])

  const fetchConversations = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true)
      const { data } = await api.get('/webchat/conversations', {
        params: {
          status,
          search: search.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      })

      const records = data.records || []
      setConversations(records)

      if (selected) {
        const freshSelected = records.find((item: WebChatConversation) => item.id === selected.id)
        if (freshSelected) setSelected(freshSelected)
      }
    } catch (error) {
      console.error('Error loading webchat conversations:', error)
      setConversations([])
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  const fetchMessages = async (conversationId: number, markRead = true) => {
    try {
      const { data } = await api.get(`/webchat/conversations/${conversationId}/messages`)
      setMessages(data.records || [])

      if (markRead) {
        await api.post(`/webchat/conversations/${conversationId}/read`)
        setConversations(prev =>
          prev.map(item => item.id === conversationId ? { ...item, unreadMessages: 0 } : item)
        )
      }
    } catch (error) {
      console.error('Error loading webchat messages:', error)
      setMessages([])
    }
  }

  const handleSelect = async (conversation: WebChatConversation) => {
    setSelected(conversation)
    await fetchMessages(conversation.id)
  }

  const handleSend = async () => {
    const text = message.trim()
    if (!text || !selected) return

    setMessage('')

    try {
      const { data } = await api.post(`/webchat/conversations/${selected.id}/messages`, {
        message: text,
      })

      setMessages(prev => [...prev, data])
      setConversations(prev =>
        prev.map(item =>
          item.id === selected.id
            ? { ...item, lastMessage: text, lastMessageAt: data.createdAt, status: 'open' }
            : item
        )
      )
    } catch (error) {
      console.error('Error sending webchat message:', error)
      setMessage(text)
    }
  }

  const handleStatusChange = async (nextStatus: 'open' | 'resolved' | 'closed') => {
    if (!selected) return

    try {
      const { data } = await api.put(`/webchat/conversations/${selected.id}/status`, {
        status: nextStatus,
      })
      setSelected(data)
      setConversations(prev => prev.map(item => item.id === data.id ? data : item))
    } catch (error) {
      console.error('Error updating webchat status:', error)
    }
  }

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return conversations

    return conversations.filter(item =>
      item.visitorName.toLowerCase().includes(term) ||
      item.sessionId.toLowerCase().includes(term) ||
      (item.lastMessage || '').toLowerCase().includes(term)
    )
  }, [conversations, search])

  const formatTime = (value?: string) => {
    if (!value) return ''
    const date = new Date(value)
    const now = new Date()
    const sameDay = date.toDateString() === now.toDateString()
    return sameDay
      ? date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
  }

  const statusLabel = (value: string) => {
    if (value === 'resolved') return 'Resuelto'
    if (value === 'closed') return 'Cerrado'
    return 'Abierto'
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ChatIcon color="primary" />
            <Box>
              <Typography level="h2">WebChat</Typography>
              <Typography level="body-sm" color="neutral">
                Conversaciones recibidas desde los widgets instalados en landings.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startDecorator={<RefreshIcon />}
            loading={loading}
            onClick={() => fetchConversations()}
          >
            Actualizar
          </Button>
        </Stack>

        <Sheet
          variant="outlined"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
            minHeight: 'calc(100vh - 190px)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <Stack sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider', minHeight: 0 }}>
            <Stack spacing={1.5} sx={{ p: 2 }}>
              <Input
                size="sm"
                placeholder="Buscar conversación"
                startDecorator={<SearchIcon />}
                value={search}
                onChange={event => setSearch(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') void fetchConversations()
                }}
              />

              {/* Filtro por estado DESHABILITADO (fallaba al cambiar). Reemplazado por el filtro
                  de rango de fechas (default últimos 30 días), igual que en Tickets.
              <Select size="sm" value={status} onChange={(_, value) => setStatus(value || 'open')}>
                <Option value="open">Abiertos</Option>
                <Option value="resolved">Resueltos</Option>
                <Option value="closed">Cerrados</Option>
                <Option value="all">Todos</Option>
              </Select>
              */}
              <FormControl size="sm" sx={{ minWidth: 220 }}>
                <FormLabel>Rango de fechas</FormLabel>
                <DateRangePicker
                  since={startDate}
                  until={endDate}
                  presetLabel=""
                  months={1}
                  showPresets={false}
                  align="left"
                  allowClear
                  showRangeInTrigger
                  fullWidth
                  placeholder="Últimos 30 días"
                  onApply={(s, u) => {
                    setStartDate(s)
                    setEndDate(u)
                  }}
                />
              </FormControl>
            </Stack>

            <Divider />

            <List sx={{ overflow: 'auto', p: 1, flex: 1 }}>
              {filteredConversations.map(conversation => (
                <ListItem key={conversation.id}>
                  <ListItemButton
                    selected={selected?.id === conversation.id}
                    onClick={() => handleSelect(conversation)}
                    sx={{ borderRadius: 8, alignItems: 'flex-start' }}
                  >
                    <Badge
                      badgeContent={conversation.unreadMessages || undefined}
                      color="danger"
                      size="sm"
                    >
                      <Avatar size="sm">{conversation.visitorName.slice(-2)}</Avatar>
                    </Badge>
                    <ListItemContent>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography level="title-sm" noWrap>
                          {conversation.visitorName}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {formatTime(conversation.lastMessageAt || conversation.updatedAt)}
                        </Typography>
                      </Stack>
                      <Typography level="body-xs" color="neutral" noWrap>
                        {conversation.widget?.name || 'Widget'} · {statusLabel(conversation.status)}
                      </Typography>
                      <Typography level="body-sm" noWrap>
                        {conversation.lastMessage || 'Sin mensajes'}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}

              {filteredConversations.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography level="body-sm" color="neutral">
                    No hay conversaciones WebChat.
                  </Typography>
                </Box>
              )}
            </List>
          </Stack>

          {selected ? (
            <Stack sx={{ minHeight: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar>{selected.visitorName.slice(-2)}</Avatar>
                  <Box>
                    <Typography level="title-md">{selected.visitorName}</Typography>
                    <Typography level="body-xs" color="neutral">
                      {selected.widget?.name || 'Widget'} · {selected.metadata?.pageUrl || selected.metadata?.origin || 'Landing'}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Chip size="sm" variant="soft" color={selected.status === 'open' ? 'success' : 'neutral'}>
                    {statusLabel(selected.status)}
                  </Chip>
                  <IconButton
                    size="sm"
                    variant="outlined"
                    color="success"
                    onClick={() => handleStatusChange('resolved')}
                    disabled={selected.status === 'resolved'}
                  >
                    <ResolvedIcon />
                  </IconButton>
                </Stack>
              </Stack>

              <Stack spacing={1.25} sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: 'background.level1' }}>
                {messages.map(item => {
                  const outbound = item.direction === 'outbound'
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        justifyContent: outbound ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '72%',
                          bgcolor: outbound ? 'primary.solidBg' : 'background.surface',
                          color: outbound ? 'primary.solidColor' : 'text.primary',
                          border: outbound ? 'none' : '1px solid',
                          borderColor: 'divider',
                          borderRadius: 8,
                          px: 1.5,
                          py: 1,
                          boxShadow: outbound ? 'sm' : 'none',
                        }}
                      >
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>
                          {item.body}
                        </Typography>
                        <Typography
                          level="body-xs"
                          sx={{ mt: 0.5, color: outbound ? 'rgba(255,255,255,0.75)' : 'text.tertiary' }}
                        >
                          {outbound ? item.sender?.name || 'Equipo' : selected.visitorName} · {formatTime(item.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
                <div ref={messagesEndRef} />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Textarea
                  minRows={1}
                  maxRows={4}
                  placeholder="Responder desde WebChat"
                  value={message}
                  onChange={event => setMessage(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault()
                      void handleSend()
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                <Button endDecorator={<SendIcon />} onClick={handleSend} disabled={!message.trim()}>
                  Enviar
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 360 }}>
              <ChatIcon sx={{ fontSize: 54, color: 'text.tertiary' }} />
              <Typography level="title-md">Selecciona una conversación</Typography>
              <Typography level="body-sm" color="neutral">
                Las respuestas se entregan al widget instalado en la landing.
              </Typography>
            </Stack>
          )}
        </Sheet>
      </Stack>
    </Container>
  )
}
